import { useEffect } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    Bundle,
    Edition,
    getPrimaryProductKeyFromBundle
} from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import { useUserContext } from "@/components/user-context";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client.tsx";

import { FormLoading } from "./components";
import {
    EmbeddedSignupForm,
    type ConfluenceAnchorVariant
} from "./components/signup-form";
import { SignupFormContextProvider } from "./components/signup-form/hooks";

export type EmbeddedSignupModuleProps = {
    bundle: Bundle;
    edition?: Edition;
    useCurrentRedirect?: boolean;
    variantOverride?: ConfluenceAnchorVariant;
    customSignupHeader?: string;
};

const EmbeddedSignupModule = ({
    bundle,
    edition = "free",
    useCurrentRedirect,
    variantOverride,
    customSignupHeader
}: EmbeddedSignupModuleProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { profile, user } = useUserContext();
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        if (
            !analyticsClient ||
            user.requestInProgress ||
            profile.requestInProgress
        ) {
            return;
        }
        if (user.isUserLoggedIn) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "siteNameComponent",
                containerId: "bxp-ui-signup",
                actionSubjectId: "embedded",
                attributes: {
                    productKey: getPrimaryProductKeyFromBundle(bundle),
                    edition,
                    userType: profile.cohorts?.userType,
                    domainType: profile.cohorts?.domainType,
                    companyType: profile.cohorts?.companyType
                }
            }).fire();
        } else {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "accountCreationComponent",
                containerId: "bxp-ui-signup",
                actionSubjectId: "embedded",
                attributes: {
                    productKey: getPrimaryProductKeyFromBundle(bundle),
                    edition
                }
            }).fire();
        }
    }, [
        createAnalyticsEvent,
        analyticsClient,
        user.isUserLoggedIn,
        user.requestInProgress,
        bundle,
        edition,
        profile.requestInProgress,
        profile.cohorts?.userType,
        profile.cohorts?.domainType,
        profile.cohorts?.companyType
    ]);

    return (
        <SignupFormContextProvider
            bundle={bundle}
            edition={edition}
            useCurrentRedirect={useCurrentRedirect}
            isEmbedded
            customHeader={customSignupHeader}
        >
            <div data-testid="signup-module">
                <div aria-busy={user.requestInProgress} aria-live="polite">
                    {user.requestInProgress ? (
                        <FormLoading notFullHeight />
                    ) : (
                        <TryIntent
                            intentType="form"
                            productKey={getPrimaryProductKeyFromBundle(bundle)}
                            edition={edition}
                        >
                            <EmbeddedSignupForm
                                bundle={bundle}
                                isUserLoggedIn={user.isUserLoggedIn}
                                variantOverride={variantOverride}
                            />
                        </TryIntent>
                    )}
                </div>
            </div>
        </SignupFormContextProvider>
    );
};

export default EmbeddedSignupModule;
